import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import Rating from '@material-ui/lab/Rating'
import * as React from 'react'

import AvatarIcon from '../../stories/assets/avatar-icon.svg'
import Avatar from '../avatar/Avatar'
import Box from '../base/Box'
import { Card, CardActionArea, CardHeader } from '../base/Card'
import Grid from '../base/Grid'
import Typography from '../base/Typography'
import Tags, { TagInterface as Tag } from '../tags/Tags'

export interface CleanerData {
  id: number
  name: string
  rating: number
  imgUrl?: string
  beforeTime: string
  afterTime: string
  beforeAfterLabel: boolean
}

export type CardCleanerProps = {
  cleanerData: CleanerData
  tags: Tag[]
  onClick(cleanerId: number): void
  isSelected: boolean
}

const BeforeAfterStyle = {
  width: 73,
  padding: 8,
  borderRadius: '4px'
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      width: 271,
      display: 'flex',
      justifyContent: 'space-between',
      borderRadius: '4px'
    },
    default: {
      border: `1px solid ${theme.palette.grey[400]}`,
      background: theme.palette.common.white
    },
    selected: {
      border: `1px solid ${theme.palette.primary.main}`,
      background: theme.palette.primary.main
    },
    cardHeader: {
      padding: 0
    },
    cardPadding: {
      padding: theme.spacing(1)
    },
    labelBeforeAfter: {
      ...BeforeAfterStyle,
      background: '#F8FAFB'
    },
    labelBeforeAfterSelected: {
      ...BeforeAfterStyle,
      color: theme.palette.common.white,
      background: 'rgba(255,255,255, 0.06)'
    },
    ratingNumber: {
      marginRight: '4px'
    },
    ratingContainer: {
      lineHeight: '28px'
    },
    Icon: {
      fontSize: '15px'
    },
    IconEmpty: {
      color: theme.palette.grey[400]
    },
    IconFilled: {
      color: '#FFAF52'
    },
    cardTag: {
      padding: '16px 0px 0px 54px',
      '&:last-child': {
        paddingBottom: 0
      }
    },
    Tag: {
      marginRight: '4px'
    },
    selectColor: {
      color: theme.palette.common.white
    }
  })
)

const CardCleaner: React.FC<CardCleanerProps> = (props) => {
  const classes = useStyles()
  const { cleanerData, isSelected, onClick, tags } = props
  return (
    <Card elevation={0}>
      <CardActionArea
        onClick={(): void => onClick(cleanerData.id)}
        classes={{ root: classes.card }}
        className={isSelected ? classes.selected : classes.default}
      >
        <Box className={classes.cardPadding}>
          <CardHeader
            className={isSelected ? classes.selectColor : 'null'}
            classes={{ root: classes.cardHeader }}
            avatar={
              <Avatar
                src={cleanerData.imgUrl ? cleanerData.imgUrl : AvatarIcon}
                imageAvatar
                customSize="large"
              />
            }
            headTitle={
              <Typography
                children={cleanerData.name ? cleanerData.name : 'Cleaner Name'}
                bodyVariants="xsMedium"
              />
            }
            subheader={
              <Grid container item>
                <Box className={classes.ratingNumber}>
                  <Typography
                    className={isSelected ? classes.selectColor : 'null'}
                    children={cleanerData.rating ? cleanerData.rating : 0}
                    variant="overline"
                  />
                </Box>
                <Box className={classes.ratingContainer}>
                  <Rating
                    classes={{
                      icon: classes.Icon,
                      iconEmpty: isSelected
                        ? classes.selectColor
                        : classes.IconEmpty,
                      iconFilled: classes.IconFilled
                    }}
                    precision={0.5}
                    value={cleanerData.rating ? cleanerData.rating : 0}
                    readOnly
                  />
                </Box>
              </Grid>
            }
          />
          <Grid classes={{ root: classes.cardTag }}>
            <Box display="flex">
              {tags.map((tag, index) => (
                <Box key={`tag-${index}`} className={classes.Tag}>
                  <Tags
                    label={tag.label}
                    customVariant={tag.variant}
                    sizes="xs"
                  />
                </Box>
              ))}
            </Box>
          </Grid>
        </Box>
        <Box
          display={cleanerData.beforeAfterLabel ? 'none' : 'null'}
          className={
            isSelected
              ? classes.labelBeforeAfterSelected
              : classes.labelBeforeAfter
          }
        >
          <Typography children="Before" variant="caption" />
          <Typography
            children={cleanerData.beforeTime ? cleanerData.beforeTime : '0 m'}
            bodyVariants="xxsMedium"
          />
          <br />
          <Typography children="After" variant="caption" />
          <Typography
            children={cleanerData.afterTime ? cleanerData.afterTime : '0 m'}
            bodyVariants="xxsMedium"
          />
        </Box>
      </CardActionArea>
    </Card>
  )
}

export default CardCleaner
